import Command from "@ckeditor/ckeditor5-core/src/command";
import { CkeditorConstants } from "../../src/utils";
import {
  subtemplateUtils,
  checkIsCitation,
  checkSelectedLink,
} from "../../src/utils/subtemplateutils";
import { SubtemplateShared as Shared } from "./subtemplates-shared";

const {
  localStorageItem: {
    enableInsertBtn,
    DATA_UNLOCK_SECTION,
    SUBTEMPLATE_LIST,
    EXISTING_SUBTEMPLATES,
    IS_LI_TAG_UP_FOR_SUB_TEMP_INSERTION,
    SUBTEMPLATE_OBJECT,
  },
  helperAttributes: {
    SUBTEMPLATE_NAME,
    SUBTEMPLATES_ALLOW,
    CONTENTEDITABLE,
    DATA_UNLOCK,
    NO_ONE,
  },
  unLockValues: { no_one },
  htmlElement: { HTML_SPAN },
} = CkeditorConstants;

export default class SubTemplatesCommand extends Command {
  constructor(editor) {
    super(editor);
    this.refresh();
  }

  execute() {
    this.editor.fire("subTemplates");
    this.isEnabled = false;
    this.dropdownList = [];
    this.refresh();
  }

  refresh() {
    this.listenTo(this.editor.editing.view.document, "click", (event, data) => {
      this.isEnabled =
        this.enableDisableIcon(data) && this.hasAnySubtemplates();
      localStorage.setItem(enableInsertBtn, JSON.stringify(this.isEnabled));
    });

    this.listenTo(this.editor.editing.view.document, "keyup", (event, data) => {
      this.isEnabled = this.enableDisableIcon(data);
    });
  }

  hasAnySubtemplates() {
    const subtemplateObject = JSON.parse(
      localStorage.getItem(SUBTEMPLATE_OBJECT)
    );
    const subTemplateList = JSON.parse(localStorage.getItem(SUBTEMPLATE_LIST));
    return (
      subtemplateObject &&
      subTemplateList &&
      subtemplateObject.some((subTemplate) =>
        subTemplateList.find(({ name }) => name === subTemplate.name)
      )
    );
  }

  enableDisableIcon(data) {
    const isLinkClicked = subtemplateUtils(this.editor, data.domTarget);
    const isCitationClicked = checkIsCitation(this.editor, data.domTarget);
    const isWholeLinkSelected = checkSelectedLink(this.editor, data.domTarget);
    const clickedElement = this.editor.model.document.selection;
    const clickedElementPosition = clickedElement.getFirstPosition();

    let isInlineSubtemplate = false;
    let anchorParent = [...clickedElement.anchor.parent.getChildren()];
    if (anchorParent.length) {
      for (const childNode of anchorParent) {
        if (
          childNode.hasAttribute(HTML_SPAN) &&
          childNode
            .getAttribute(HTML_SPAN)
            .attributes?.hasOwnProperty(SUBTEMPLATE_NAME)
        ) {
          isInlineSubtemplate = true;
        }
      }
    }

    /**
     * !isLinkClicked -> to check selection is not on the link ||
     * (isLinkClicked && (clickedElementPosition.isAtStart || clickedElementPosition.isAtEnd)) -> to check selection has done at the start or end of the link
     * && !isCitationClicked -> citation is not clicked/not in the selection of text
     * && isWholeLinkSelected !== false -> to check link first letter/several letters are selected.
     */
    if (
      !isInlineSubtemplate &&
      (!isLinkClicked ||
        (isLinkClicked &&
          (clickedElementPosition.isAtStart ||
            clickedElementPosition.isAtEnd))) &&
      !isCitationClicked &&
      isWholeLinkSelected !== false
    ) {
      if (localStorage.getItem(DATA_UNLOCK_SECTION)) {
        localStorage.removeItem(DATA_UNLOCK_SECTION);
      }

      if (localStorage.getItem(IS_LI_TAG_UP_FOR_SUB_TEMP_INSERTION)) {
        localStorage.removeItem(IS_LI_TAG_UP_FOR_SUB_TEMP_INSERTION);
      }

      let [selection, { targetEl, isTargetContainer }] =
        this.getSelectedElement(data);
      const targetName = targetEl?.name || targetEl?.tagName.toLowerCase();

      if (
        selection?.hasAttribute(SUBTEMPLATES_ALLOW) &&
        selection?.getAttribute(DATA_UNLOCK) === no_one
      ) {
        if (
          selection.hasAttribute(NO_ONE) &&
          !localStorage.getItem(DATA_UNLOCK_SECTION)
        ) {
          localStorage.setItem(
            DATA_UNLOCK_SECTION,
            selection.getAttribute(NO_ONE)
          );
        }
      }

      const existingSubtemplates = JSON.parse(
        localStorage.getItem(EXISTING_SUBTEMPLATES)
      );
      const disabledSubtemplate = existingSubtemplates
        ? existingSubtemplates.length === 0
        : false;
      const checkForSubtemplate =
        
        selection?.hasAttribute(SUBTEMPLATES_ALLOW) &&
        (selection?.getAttribute(DATA_UNLOCK) === no_one ||
          selection?.getAttribute(CONTENTEDITABLE) === "true") &&
        !disabledSubtemplate;
      if (!isLinkClicked) {
        return Boolean(checkForSubtemplate && isTargetContainer);
      }
      if (
        isLinkClicked &&
        (clickedElementPosition.isAtStart || clickedElementPosition.isAtEnd)
      ) {
        return Boolean(checkForSubtemplate);
      }
    } else {
      return null;
    }
  }

  getSelectedElement(data) {
    const domTarget = data?.domTarget;
    const isNonEditable = /^false$/i.test(
      domTarget?.getAttribute(CONTENTEDITABLE)
    );
    let containerEl;
    let subtemplateAttrEl;
    let isHeading;
    let isDivSubtemplateEl;
    let targetEl;
    let isListItem = Shared.isDomListItem(domTarget.tagName);
    let isParentListItem = Shared.isDomListItem(
      domTarget?.parentElement?.tagName
    );
    let isTargetContainer;
    if (isNonEditable) {
      targetEl = domTarget;
      isTargetContainer = Shared.isContainerDomElement(targetEl);
      containerEl = Shared.getDomContainerElement(domTarget);
      isHeading = Shared.isDomHeading(containerEl.tagName);
      subtemplateAttrEl = Shared.getSubtemplateAttrDomElement(containerEl);
      isDivSubtemplateEl = Shared.isDivElement(subtemplateAttrEl.tagName);
    } else {
      targetEl = Shared.getDocumentSelectedElement(this.editor.model.document);
      isTargetContainer = Shared.isContainerEditorElement(targetEl);
      containerEl = Shared.getEditorContainerElement(targetEl);
      isHeading = Shared.isEditorHeading(containerEl.name);
      subtemplateAttrEl = Shared.getSubtemplateAttrEditorElement(containerEl);
      isDivSubtemplateEl = Shared.isDivElement(subtemplateAttrEl.name);
    }

    if (isHeading && containerEl.getAttribute("property") === "dcterms:title") {
      return [containerEl, { targetEl, isTargetContainer }];
    } else {
      let isViewSelectionHasLi;
      if (
        this.editor.editing.view.document?.selection?.anchor
      ) {
        isViewSelectionHasLi =
          this.editor.editing.view.document.selection.anchor
            .getAncestors()
            .reverse()
            .some((item) => item.name === "li");
      }

      if (
        (isListItem || isParentListItem || isViewSelectionHasLi) &&
        !isNonEditable
      ) {
        localStorage.setItem(IS_LI_TAG_UP_FOR_SUB_TEMP_INSERTION, "true");
      }
      isNonEditable && localStorage.removeItem(IS_LI_TAG_UP_FOR_SUB_TEMP_INSERTION);
      if (isDivSubtemplateEl && data.domTarget.hasAttribute(NO_ONE)) {
        localStorage.setItem(
          DATA_UNLOCK_SECTION,
          data.domTarget.getAttribute(NO_ONE)
        );
      }

      return [subtemplateAttrEl, { targetEl, isTargetContainer }];
    }
  }
}
